import React, { createContext, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { formatNumber, getFirstTwoDecimals } from "../utils/utils";

const PnlSharedContext = createContext();

export const PnlSharedProvider = ({
  children,
  plValues,
  plStructure,
  benchmark,
  netRevenueAnchor,
  combinedAboveAnchor,
  revenueReference,
  serveMarginAnchor,
  oom,
  t,
}) => {
  // Simplified OOM calculations
  const { oomData, showValueByVolumeDecimalsData } = useMemo(() => {
    // Calculate median serve margin per volume for decimal display logic
    const serveMarginsPerVolume = Object.values(plValues)
      .filter(
        (value) => value && value[serveMarginAnchor.current] && value.volume,
      )
      .map((value) => value[serveMarginAnchor.current] / value.volume)
      .sort((a, b) => a - b);

    const medianPerVolume =
      serveMarginsPerVolume.length > 0
        ? serveMarginsPerVolume.length % 2 === 0
          ? (serveMarginsPerVolume[serveMarginsPerVolume.length / 2 - 1] +
              serveMarginsPerVolume[serveMarginsPerVolume.length / 2]) /
            2
          : serveMarginsPerVolume[Math.floor(serveMarginsPerVolume.length / 2)]
        : 0;

    return {
      oomData: oom || { div: 1e6, x: " " + (t("oom.m") || "m") },
      showValueByVolumeDecimalsData: medianPerVolume < 100,
    };
  }, [plValues, oom, t, serveMarginAnchor]);

  // Optimized criticality level calculations
  const criticalityLevels = useMemo(() => {
    const levels = {};
    // Filter out undefined/null values from plValues
    const plKeys = Object.keys(plValues).filter((key) => plValues[key] != null);

    // Pre-calculate first P&L references if needed
    const firstPnlRefs = {};
    if (benchmark === "first_pnl") {
      const firstPl = plKeys.find((key) => plValues[key]);
      if (firstPl) {
        const revenueRef = plValues[firstPl][netRevenueAnchor.current];
        if (revenueRef > 0) {
          plStructure.current.forEach((line) => {
            const value = plValues[firstPl][line.meta_name];
            if (value !== undefined) {
              firstPnlRefs[line.meta_name] = value / revenueRef;
            }
          });
        }
      }
    }

    // Pre-calculate line metadata
    const netIdx = plStructure.current.findIndex(
      (l) => l.meta_name === netRevenueAnchor.current,
    );
    const lineMetadata = plStructure.current.reduce((acc, line, lineIdx) => {
      let refValue = null;
      if (line.ref_values) {
        if (revenueReference === "combined") {
          const anchor =
            lineIdx < netIdx
              ? combinedAboveAnchor?.current
              : netRevenueAnchor.current;
          refValue = anchor ? line.ref_values[anchor] : null;
        } else if (revenueReference) {
          refValue = line.ref_values[revenueReference] ?? null;
        }
      }
      acc[line.meta_name] = {
        ref_value: refValue,
        is_total: line.level === 1,
      };
      return acc;
    }, {});

    // Calculate criticality levels
    plKeys.forEach((numPL) => {
      const plNum = parseInt(numPL);
      if (!plValues[plNum]) {
        plStructure.current.forEach((line) => {
          levels[`${plNum}-${line.meta_name}`] = "8";
        });
        return;
      }

      plStructure.current.forEach((line, lineIdx) => {
        const metaName = line.meta_name;
        let activeAnchor;
        if (revenueReference === "combined") {
          activeAnchor =
            lineIdx < netIdx
              ? combinedAboveAnchor?.current
              : netRevenueAnchor.current;
        } else {
          activeAnchor = revenueReference || netRevenueAnchor.current;
        }
        const revenue = plValues[plNum][activeAnchor];
        if (!revenue || revenue <= 0) {
          levels[`${plNum}-${metaName}`] = "8";
          return;
        }
        const val = plValues[plNum][metaName] / revenue;
        const metadata = lineMetadata[metaName];

        const ref =
          benchmark === "total_company"
            ? metadata.ref_value
            : benchmark === "first_pnl"
              ? firstPnlRefs[metaName] || 0
              : 0;

        if (ref === 0) {
          levels[`${plNum}-${metaName}`] = "8";
          return;
        }

        const min = ref * 0.8;
        const max = ref * 1.2;
        const range = max - min;

        if (range === 0) {
          levels[`${plNum}-${metaName}`] = "8";
          return;
        }

        const normalizedValue = Math.min(
          Math.max(Math.round((14 * (val - min)) / range), 1),
          14,
        );

        levels[`${plNum}-${metaName}`] = metadata.is_total
          ? (1 + normalizedValue).toString()
          : (15 - normalizedValue).toString();
      });
    });

    return levels;
  }, [
    plValues,
    netRevenueAnchor,
    benchmark,
    plStructure,
    revenueReference,
    combinedAboveAnchor,
  ]);

  // Shared memoized functions
  const sharedFunctions = useMemo(
    () => ({
      // Optimized getCriticalityLevel function using pre-calculated values
      getCriticalityLevel: (numPL, meta_name) => {
        return criticalityLevels[`${numPL}-${meta_name}`] || "8";
      },

      // Simplified value formatting function
      formatValue: (
        value,
        meta_name,
        numPL,
        showValueType,
        showValueByVolumeDecimals,
      ) => {
        if (!value) return "-";

        let actualValue = value;
        let actualVolume = plValues[numPL]?.volume;

        if (Array.isArray(value)) {
          actualValue = value[0];
          actualVolume = value[2];
        }

        if (showValueType === "value") {
          const rawValue = actualValue / oomData.div;
          const decimalPlaces =
            oomData.div >= 1e6 ? 2 : oomData.div >= 1e3 ? 1 : 0;
          const formattedValue = rawValue.toFixed(decimalPlaces);
          const parts = formattedValue.split(".");
          const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

          return decimalPlaces > 0 && parts.length > 1
            ? `${integerPart}.${parts[1]}`
            : integerPart;
        } else {
          // Value by volume formatting
          if (!actualVolume) return "-";

          const valuePerVolume = actualValue / actualVolume;
          return showValueByVolumeDecimals
            ? `${formatNumber(valuePerVolume)}.${getFirstTwoDecimals(valuePerVolume)}`
            : formatNumber(valuePerVolume);
        }
      },

      // Simplified percentage value calculation
      getPlPercentValue: (numPL, name) => {
        const lineValue = plValues[numPL]?.[name];

        if (Array.isArray(lineValue)) {
          const [value, revenue] = lineValue;
          return revenue ? `${((100 * value) / revenue).toFixed(1)}%` : "-";
        }

        let activeAnchor;
        if (revenueReference === "combined" && plStructure?.current) {
          const lineIdx = plStructure.current.findIndex(
            (l) => l.meta_name === name,
          );
          const netIdx = plStructure.current.findIndex(
            (l) => l.meta_name === netRevenueAnchor.current,
          );
          activeAnchor =
            lineIdx < netIdx
              ? combinedAboveAnchor?.current
              : netRevenueAnchor.current;
        } else {
          activeAnchor = revenueReference || netRevenueAnchor.current;
        }
        const revenue = plValues[numPL]?.[activeAnchor];
        return revenue ? `${((100 * lineValue) / revenue).toFixed(1)}%` : "-";
      },

      // Shared data
      oomData,
      showValueByVolumeDecimalsData,
      benchmark,
    }),
    [
      criticalityLevels,
      oomData,
      plValues,
      plStructure,
      netRevenueAnchor,
      combinedAboveAnchor,
      revenueReference,
      showValueByVolumeDecimalsData,
      benchmark,
    ],
  );

  return (
    <PnlSharedContext.Provider value={sharedFunctions}>
      {children}
    </PnlSharedContext.Provider>
  );
};

export const usePnlShared = () => {
  const context = useContext(PnlSharedContext);
  if (!context) {
    throw new Error("usePnlShared must be used within PnlSharedProvider");
  }
  return context;
};

// PropTypes validation
PnlSharedProvider.propTypes = {
  children: PropTypes.node.isRequired,
  plValues: PropTypes.object.isRequired,
  plStructure: PropTypes.object.isRequired,
  benchmark: PropTypes.string,
  netRevenueAnchor: PropTypes.object.isRequired,
  combinedAboveAnchor: PropTypes.object,
  revenueReference: PropTypes.string,
  serveMarginAnchor: PropTypes.object.isRequired,
  oom: PropTypes.object,
  t: PropTypes.func.isRequired,
};

PnlSharedProvider.defaultProps = {
  benchmark: "total_company",
  combinedAboveAnchor: null,
  revenueReference: null,
  oom: null,
};
